import { SessionTemplateCreateDto } from "../../dto/sessions/session-template-create.dto";
import { SessionTemplateUpdateDto } from "../../dto/sessions/session-template-update.dto";
import { SessionTemplateBasicsDto, SessionTemplateDto } from "../../dto/sessions/session-template.dto";
import { ImageAssetUpload } from "../../models/media/cover.model";
import { AudienceType, DEFAULT_SESSION_TEMPLATE_WEIGHT, SessionTemplate, SessionTemplateBase, TargetGender } from "../../models/sessions/session-template.model";
import { StoreStatus } from "../../models/sessions/store-status.enum";
import { ImageAssetUploadMapper } from "../media/image-asset-upload.mapper";
import { SessionTemplateBlockMapper } from "./session-template-block.mapper";

export class SessionTemplateMapper {
  public static toModel(dto: SessionTemplateDto): SessionTemplate {
    return {
      id: dto.id,
      externalId: dto.external_id,
      title: dto.title,
      originalLanguageId: dto.original_language_id,
      subtitle: dto.subtitle,
      description: dto.description,
      placeholderTitle: dto.placeholder_title,
      estimatedDuration: dto.estimated_duration || 0,
      sessionCategoryId: dto.session_category_id,
      therapeuticDomainId: dto.therapeutic_domain_id,
      creator: 'admin',
      storeStatus: dto.store_status as StoreStatus,
      coverUrl: dto.cover_url,
      blocks: dto.blocks.map(b => SessionTemplateBlockMapper.toModel(b)),
      smartSurveyId: dto.smart_survey_id,
      coreChoiceQuestion: dto.core_choice_question,
      coreChoiceConstraint: dto.core_choice_constraint,
      refinerQuestion: dto.refiner_question,
      refinerConstraint: dto.refiner_constraint,
      leadingSilence: dto.leading_silence,
      trailingSilence: dto.trailing_silence,
      audienceType: dto.audience_type as AudienceType,
      targetGender: dto.target_gender as TargetGender ?? TargetGender.Everyone,
      isUniversal: dto.is_universal,
      weight: dto.weight ?? DEFAULT_SESSION_TEMPLATE_WEIGHT,
      internalReference: dto.internal_reference,
      updatedAt: new Date(dto.updated_at),
      createdAt: new Date(dto.created_at),
      currentVersionNumber: dto.current_version_number ?? 0,
      adminTitle: dto.internal_reference ? `${dto.internal_reference} - ${dto.title}` : dto.title
    }
  }

  public static toBasicModel(dto: SessionTemplateBasicsDto): SessionTemplate {
    return {
      id: dto.id,
      externalId: dto.external_id,
      title: dto.title,
      originalLanguageId: 1,
      subtitle: dto.subtitle,
      description: '',
      placeholderTitle: '',
      estimatedDuration: 0,
      sessionCategoryId: dto.session_category_id,
      therapeuticDomainId: dto.therapeutic_domain_id,
      creator: 'admin',
      storeStatus: dto.store_status as StoreStatus,
      coverUrl: dto.cover_url,
      blocks: [],
      smartSurveyId: undefined,
      coreChoiceQuestion: undefined,
      coreChoiceConstraint: undefined,
      refinerQuestion: undefined,
      refinerConstraint: undefined,
      leadingSilence: 0,
      trailingSilence: 0,
      audienceType: dto.audience_type as AudienceType,
      targetGender: dto.target_gender as TargetGender ?? TargetGender.Everyone,
      isUniversal: dto.is_universal,
      weight: dto.weight ?? DEFAULT_SESSION_TEMPLATE_WEIGHT,
      internalReference: dto.internal_reference,
      updatedAt: new Date(),
      createdAt: new Date(),
      currentVersionNumber: 0,
      adminTitle: dto.internal_reference ? `${dto.internal_reference} - ${dto.title}` : dto.title
    }
  }

  public static toCreateDto(model: SessionTemplateBase, languageId: number, coverUpload?: ImageAssetUpload): SessionTemplateCreateDto {
    return {
      title: model.title,
      subtitle: model.subtitle,
      description: model.description,
      placeholder_title: model.placeholderTitle,
      language_id: languageId,
      estimated_duration: model.estimatedDuration,
      therapeutic_domain: model.therapeuticDomainId,
      session_category_id: model.sessionCategoryId,
      cover: coverUpload ? ImageAssetUploadMapper.toDto(coverUpload) : undefined,
      blocks: model.blocks.map(b => SessionTemplateBlockMapper.toUpdateDto(b)),
      smart_survey_id: model.smartSurveyId,
      leading_silence: model.leadingSilence,
      trailing_silence: model.trailingSilence,
      audience_type: model.audienceType,
      target_gender: model.targetGender,
      is_universal: model.isUniversal,
      weight: model.weight,
      internal_reference: model.internalReference,
      core_choice_question: !!model.coreChoiceQuestion ? model.coreChoiceQuestion : undefined,
      core_choice_constraint: !!model.coreChoiceConstraint ? model.coreChoiceConstraint : undefined,
      refiner_question: !!model.refinerQuestion ? model.refinerQuestion : undefined,
      refiner_constraint: !!model.refinerConstraint ? model.refinerConstraint : undefined
    }
  }

  public static toUpdateDto(model: SessionTemplate, coverUpload?: ImageAssetUpload): SessionTemplateUpdateDto {
    return {
      smart_survey_id: model.smartSurveyId,
      cover: coverUpload ? ImageAssetUploadMapper.toDto(coverUpload) : undefined,
      session_category_id: model.sessionCategoryId,
      estimated_duration: model.estimatedDuration,
      blocks: model.blocks.map(b => SessionTemplateBlockMapper.toUpdateDto(b)),
      therapeutic_domain: model.therapeuticDomainId,
      leading_silence: model.leadingSilence,
      trailing_silence: model.trailingSilence,
      audience_type: model.audienceType,
      target_gender: model.targetGender,
      internal_reference: model.internalReference,
      weight: model.weight
    }
  }
}